import { useEffect, useRef, useState } from 'react';

function pickPos(pick) {
  if (pick != null && typeof pick === 'object' && pick.pos != null) return pick.pos;
  return pick;
}

function pickLabel(pick, fallback = '') {
  if (pick != null && typeof pick === 'object' && pick.label != null) return pick.label;
  if (fallback) return fallback;
  return String(pick ?? '');
}

function useCoarsePointer() {
  const [coarse, setCoarse] = useState(() => {
    if (typeof window === 'undefined') return false;
    return (
      (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) ||
      window.matchMedia?.('(pointer: coarse)')?.matches
    );
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(pointer: coarse)');
    const sync = () => setCoarse(mq.matches || (navigator.maxTouchPoints || 0) > 0);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return coarse;
}

/**
 * Pointer-based touch drag to elements marked with data-drop-pos (or custom attr).
 * Mirrors useYogaTouchDrag — HTML5 drag on fine pointers, pointer drag on coarse.
 */
export default function useTouchDragDrop({
  onDrop,
  enabled = true,
  dropAttr = 'data-drop-pos',
}) {
  const isCoarse = useCoarsePointer();
  const allowHtml5Drag = !isCoarse;
  const pickRef = useRef(null);
  const dragIdRef = useRef({ mode: '', id: null });
  const lastClientRef = useRef({ x: 0, y: 0, valid: false });
  const onDropRef = useRef(onDrop);
  const [ghost, setGhost] = useState({ visible: false, x: 0, y: 0, label: '' });
  const [hoverDropPos, setHoverDropPos] = useState(null);

  useEffect(() => {
    onDropRef.current = onDrop;
  }, [onDrop]);

  useEffect(() => {
    if (!enabled) return undefined;

    const findDropAt = (x, y) => {
      const el = document.elementFromPoint(x, y);
      const zone = el?.closest?.(`[${dropAttr}]`);
      if (!zone) return null;
      const raw = zone.getAttribute(dropAttr);
      if (raw == null || raw === '') return null;
      const n = Number.parseInt(raw, 10);
      return Number.isNaN(n) ? null : n;
    };

    const finish = (clientX, clientY, preferLast = false) => {
      const pick = pickRef.current;
      if (pick == null) return;
      let x = clientX;
      let y = clientY;
      const last = lastClientRef.current;
      if (preferLast && last.valid) {
        x = last.x;
        y = last.y;
      } else if (last.valid) {
        const bad =
          !Number.isFinite(x) || !Number.isFinite(y) || (Math.abs(x) < 1 && Math.abs(y) < 1);
        if (bad) {
          x = last.x;
          y = last.y;
        }
      }
      const dropPos = findDropAt(x, y);
      pickRef.current = null;
      dragIdRef.current = { mode: '', id: null };
      lastClientRef.current = { x: 0, y: 0, valid: false };
      setGhost({ visible: false, x: 0, y: 0, label: '' });
      setHoverDropPos(null);
      if (dropPos != null) {
        onDropRef.current?.(dropPos, pickPos(pick));
      }
    };

    const onPointerMove = (e) => {
      if (pickRef.current == null) return;
      const hasTouch = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
      if (e.pointerType === 'mouse' && !hasTouch) return;
      if (
        dragIdRef.current.mode === 'pointer' &&
        dragIdRef.current.id != null &&
        e.pointerId !== dragIdRef.current.id
      ) {
        return;
      }
      lastClientRef.current = { x: e.clientX, y: e.clientY, valid: true };
      setGhost({
        visible: true,
        x: e.clientX,
        y: e.clientY,
        label: pickLabel(pickRef.current),
      });
      setHoverDropPos(findDropAt(e.clientX, e.clientY));
    };

    const onPointerEnd = (e) => {
      const hasTouch = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
      if (e.pointerType === 'mouse' && !hasTouch) return;
      if (
        dragIdRef.current.mode === 'pointer' &&
        dragIdRef.current.id != null &&
        e.pointerId !== dragIdRef.current.id
      ) {
        return;
      }
      finish(e.clientX, e.clientY, false);
    };

    const onPointerCancel = (e) => {
      finish(e.clientX, e.clientY, true);
    };

    window.addEventListener('pointermove', onPointerMove, true);
    window.addEventListener('pointerup', onPointerEnd, true);
    window.addEventListener('pointercancel', onPointerCancel, true);
    return () => {
      window.removeEventListener('pointermove', onPointerMove, true);
      window.removeEventListener('pointerup', onPointerEnd, true);
      window.removeEventListener('pointercancel', onPointerCancel, true);
    };
  }, [enabled, dropAttr]);

  const startPointerPick = (e, pick, label = '') => {
    if (!enabled || allowHtml5Drag || pick == null) return;
    e.preventDefault();
    pickRef.current = pick;
    dragIdRef.current = { mode: 'pointer', id: e.pointerId };
    lastClientRef.current = { x: e.clientX, y: e.clientY, valid: true };
    setGhost({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      label: pickLabel(pick, label),
    });
  };

  return {
    allowHtml5Drag,
    ghost,
    hoverDropPos,
    startPointerPick,
  };
}
